import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import Select from 'react-select';
import type { StylesConfig, MultiValue } from 'react-select';
import SummernoteCorgee from '../Globals/SummernoteCorgee';
import CorgeeModal, { type CorgeeModalRef } from '../Globals/CorgeeModal';
import CorgeeWarning from '../Globals/CorgeeWarning';
import FileUpload, { type FileUploadRef } from '../Globals/FileUpload';
import CustomFields from '../CustomFields/CustomFields';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

import { duplicateCardService } from '../../services/duplicateCardService';
import { apiService } from '@/services/api';
import { socketService } from '@/services/socketService';
import ConversationApi from '../ConversationItem/api/ConversationApi';
import CardConversationLayers from '../Globals/CardConversationLayers';
import './DuplicateCard.css';
import { dashboardGroupsService } from '../DashboardGroups';
import { MAX_BODY_BYTES, BODY_TOO_LARGE_MESSAGE } from '@lib/constants';
import { byteLength } from '@lib/utils';

export interface DuplicateCardConfig {
  apiUrl: string;
  authToken: string;
  SocketConnectionId?: string;
  attachReactFunctions?: (funcs: any) => void;
  DashboardId: number;
  functions: {
    loadingBarStart?: () => void;
    loadingBarComplete?: () => void;
    conversationResponse?: (requestConfig: any, response: any) => void;
    onUpdateConversation?: (id: number, attrs: any, b: boolean, scope: string) => void;
    addLayerToCard?: (e: any, conversation: any, layer: any, cb: (layerId: number) => void) => void;
    addConversation?: (conversation: any, dashboardGroupId: number) => void;
  };
  params: {
    TitleCard?: string;
    SupportLayer?: string;
    SupportConfigValue?: string;
    SupportProjectId?: number;
    Conversation: any;
  };
}

export interface DuplicateCardProps {
  config: DuplicateCardConfig;
  userData?: any;
  UpdateCustomFields?: any;
}

export interface DuplicateCardRef {
  openModal: (conv?: any) => void;
  closeModal: () => void;
}

const DUPLICATE_FAILED_MESSAGE = 'The card could not be duplicated. Please try again.';

const DuplicateCard = forwardRef<DuplicateCardRef, DuplicateCardProps>(
  ({ config, UpdateCustomFields }, ref) =>
  {
    const [api] = useState({ url: config.apiUrl, token: config.authToken });
    const [conversation, setConversation] = useState<any>(config.params.Conversation || {});
    const [emptyConversation] = useState<any>(config.params.Conversation || {});
    const [groups, setGroups] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedProject, setSelectedProject] = useState<any>(null);

    const [layersSelected, setLayersSelected] = useState<any[]>([]);
    const [groupIdsSelected, setGroupIdsSelected] = useState<number[]>([]);
    const [groupsSelected, setGroupsSelected] = useState<any[]>([]);

    const [showTitleWarning, setShowTitleWarning] = useState(false);
    // Save failures that aren't title/layer related
    const [saveWarning, setSaveWarning] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [showGroups, setShowGroups] = useState(false);
    const [showCustomFields, setShowCustomFields] = useState(true);
    const showSummernote = true;
    const [showLayerWarning, setShowLayerWarning] = useState(false);
    // Computed: show warning when a selected group has NONE of the selected layers
    const showGroupWarning = useMemo(() =>
    {
      if ((conversation.ConfigValueId ?? conversation.ConfigValueID) !== 9) return false;
      if (groupsSelected.length === 0 || layersSelected.length === 0) return false;
      const selectedLayerIds = new Set(layersSelected.map((l: any) => l.LayerId));
      // Cross-reference with full groups catalog to get GroupLayers
      const selectedGroupIds = new Set(groupsSelected.map((g: any) => g.GroupId ?? g.Id));
      const fullSelectedGroups = groups.filter((g: any) => selectedGroupIds.has(g.GroupId ?? g.Id));
      return fullSelectedGroups.some((group: any) =>
      {
        const groupLayerIds: number[] = (group.GroupLayers || []).map((gl: any) => gl.LayerId);
        return !groupLayerIds.some((id: number) => selectedLayerIds.has(id));
      });
    }, [
      conversation.ConfigValueId,
      conversation.ConfigValueID,
      groups,
      groupsSelected,
      layersSelected,
    ]);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [isImportant, setIsImportant] = useState<boolean>(
      config.params.Conversation?.IsImportant || false,
    );

    const [layersCustomFieldsOriginal, setLayersCustomFieldsOriginal] = useState<any[]>([]);
    const [layersCustomFieldsChanges, setLayersCustomFieldsChanges] = useState<any[]>([]);
    const [isEnabledSaveAndNew, setIsEnabledSaveAndNew] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [editingPhaseId, setEditingPhaseId] = useState<number | null>(null);
    const [editingPhaseTitle, setEditingPhaseTitle] = useState('');

    const [_confirmCloseAction, setConfirmCloseAction] = useState(false);

    const activePhase = useMemo(() =>
    {
      if (tabIndex > 0 && conversation?.Phases && conversation.Phases.length >= tabIndex)
      {
        return conversation.Phases[tabIndex - 1];
      }
      return {
        ConversationPhaseId: 0,
        CoreTaskId: conversation?.CoreTaskId ?? conversation?.CoreTaskID,
      };
    }, [tabIndex, conversation?.Phases, conversation?.CoreTaskId, conversation?.CoreTaskID]);

    const acceptPhaseRename = useCallback(
      (phase: any) =>
      {
        if (!editingPhaseTitle.trim())
        {
          setEditingPhaseId(null);
          return;
        }

        const newPhases = [...(conversation.Phases || [])];
        const found = newPhases.find(
          (p: any) => p.ConversationPhaseId === phase.ConversationPhaseId,
        );
        if (found)
        {
          found.Title = editingPhaseTitle;
        }
        setConversation((prev: any) => ({ ...prev, Phases: newPhases }));
        setEditingPhaseId(null);

        const item = {
          ConversationId:
            phase.ConversationId || phase.ConversationID || conversation.ConversationId,
          ConversationPhaseId: phase.ConversationPhaseId,
          Title: editingPhaseTitle,
        };
        ConversationApi.changeNameCard(api, item).catch((e: any) => console.error(e));
      },
      [api, conversation, editingPhaseTitle],
    );

    const modalRef = useRef<CorgeeModalRef>(null);
    const addLayerModalRef = useRef<CorgeeModalRef>(null);
    const confirmCloseActionRef = useRef<CorgeeModalRef>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const summernoteRef = useRef<any>(null);
    const fileUploadRef = useRef<FileUploadRef>(null);

    const [pendingAddLayer, setPendingAddLayer] = useState<any>(null);
    const [pendingAddLayerCallback, setPendingAddLayerCallback] = useState<any>(null);

    const handleConfirmAddLayer = useCallback(() =>
    {
      if (!pendingAddLayer) return;

      const conversationCopy = { ...conversation };
      conversationCopy.ConversationLayers = [...(conversationCopy.ConversationLayers || [])];

      if (
        !conversationCopy.ConversationLayers.some((l: any) => l.LayerId === pendingAddLayer.LayerId)
      )
      {
        conversationCopy.ConversationLayers.push(pendingAddLayer);
      }

      if (conversationCopy.ConversationId)
      {
        duplicateCardService
          .UpdateProject(conversationCopy)
          .then(() =>
          {
            if (pendingAddLayerCallback && pendingAddLayerCallback.onUpdateLayerToCard)
            {
              pendingAddLayerCallback.onUpdateLayerToCard(pendingAddLayer.LayerId);
            }
            setConversation(conversationCopy);
            const selected = conversationCopy.ConversationLayers || [];
            setLayersSelected(selected);
            setShowLayerWarning(selected.length === 0);

            if (config.functions?.onUpdateConversation)
            {
              config.functions.onUpdateConversation(
                conversation.ConversationId,
                { ConversationLayers: conversationCopy.ConversationLayers },
                true,
                '',
              );
            }

            addLayerModalRef.current?.closeModal();
          })
          .catch(err =>
          {
            console.error('Failed to add layer to card:', err);
            addLayerModalRef.current?.closeModal();
          });
      }
      else
      {
        // New card not yet saved, apply locally
        setConversation(conversationCopy);
        const selected = conversationCopy.ConversationLayers || [];
        setLayersSelected(selected);
        setShowLayerWarning(selected.length === 0);
        if (pendingAddLayerCallback && pendingAddLayerCallback.onUpdateLayerToCard)
        {
          pendingAddLayerCallback.onUpdateLayerToCard(pendingAddLayer.LayerId);
        }
        addLayerModalRef.current?.closeModal();
      }
    }, [pendingAddLayer, pendingAddLayerCallback, conversation, config]);

    const handleAddLayerToProject = useCallback(
      async (
        _e: any,
        layer: any,
        functions?: { onUpdateLayerToCard?: (layerId: number) => void },
      ) =>
      {
        let projectConversationId =
          layer?.ProjectConversationId ??
          conversation.ProjectConversationId ??
          (selectedProject as any)?.ConversationId;

        if (!projectConversationId && conversation.ProjectId)
        {
          try
          {
            const proj = await apiService.get<any>(
              `Project/GetProject?projectId=${conversation.ProjectId}`,
            );
            projectConversationId = proj?.ConversationId;
          }
          catch (_) { /* best-effort */ }
        }

        if (!projectConversationId) return;

        const layerName = layer.Name || layer.LayerName || '';
        const layerColor = layer.Color || layer.LayerColor || '';

        try
        {
          const projectConv = await apiService.post<any>(
            `conversation/GetConversationById?conversationId=${projectConversationId}`,
            null,
          );
          if (projectConv && projectConv.ConfigValueId !== 15)
          {
            let fetchedGroups: any[] = [];
            try
            {
              const result = await apiService.get<any[]>(
                `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
              );
              if (Array.isArray(result)) fetchedGroups = result;
            }
            catch (_) { /* best-effort */ }

            await apiService.post<any>('conversation/updateconversation', {
              ...projectConv,
              CustomFields: [],
              ConversationGroups: fetchedGroups.length > 0 ? fetchedGroups : projectConv.ConversationGroups,
              ConversationLayers: [
                ...(projectConv.ConversationLayers || []),
                {
                  ConversationId: projectConv.ConversationId,
                  LayerId: layer.LayerId,
                  LayerName: layerName,
                  LayerColor: layerColor,
                  isForAdd: true,
                  IsActive: 1,
                },
              ],
              SocketConnectionId: socketService.connectionId,
            });
          }
        }
        catch (err)
        {
          console.error('Failed to add layer to project:', err);
        }

        functions?.onUpdateLayerToCard?.(layer.LayerId);
      },
      [conversation.ProjectConversationId, conversation.ProjectId, selectedProject],
    );

    const InitConfig = useCallback(
      (conv: any) =>
      {
        // Deep-clone to isolate DuplicateCard state from the parent (InfoWindow/ConversationDetailsView)
        // so that edits here don't leak until "Update" is clicked
        const clonedConv = JSON.parse(JSON.stringify(conv));
        setConversation(clonedConv);
        setGroups([]);
        setProjects([]);
        setSelectedProject(null);
        setLayersCustomFieldsOriginal([]);
        setLayersCustomFieldsChanges([]);
        setIsImportant(conv.IsImportant);
        setShowGroups(false);

        // Use the phaseSelected prop to set the initial tabIndex for DuplicateCard
        let initialTabIndex = 0;
        if (
          conv.phaseSelected &&
          conv.phaseSelected.ConversationPhaseId > 0 &&
          Array.isArray(conv.Phases)
        )
        {
          const foundIndex = conv.Phases.findIndex(
            (p: any) => p.ConversationPhaseId === conv.phaseSelected.ConversationPhaseId,
          );
          if (foundIndex >= 0)
          {
            initialTabIndex = foundIndex + 1;
          }
        }
        setTabIndex(initialTabIndex);
        setEditingPhaseId(null);

        // Fetch projects for this card type
        const cvId = conv.ConfigValueId ?? conv.ConfigValueID;
        if (cvId)
        {
          duplicateCardService
            .GetProjectsByConfigValueId(cvId)
            .then(projectList =>
            {
              const list = projectList || [];
              setProjects(list);
              const current = list.find((p: any) => (p.ProjectId ?? p.Id) === conv.ProjectId);
              setSelectedProject(current || null);
            })
            .catch(() =>
            {
              /* silently ignore */
            });
        }

        // Pre-populate groups from existing conversation
        const existingGroups: any[] = conv.ConversationGroups || [];
        setGroupsSelected(existingGroups);
        setGroupIdsSelected(existingGroups.map((g: any) => g.GroupId ?? g.Id));

        // Pre-populate layers from existing conversation (exclude virtual layer 0)
        const existingLayers: any[] = (conv.ConversationLayers || []).filter(
          (l: any) => l.LayerId !== 0,
        );
        setLayersSelected(existingLayers);

        const isProject = (conv.ConfigValueId ?? conv.ConfigValueID) === 9;

        if (isProject)
        {
          // For projects: fetch ALL groups as options, project groups as selected
          Promise.all([
            duplicateCardService.GetGroups(), // All groups (catalog)
            duplicateCardService.GetGroups(conv.ProjectId), // Project-specific groups
          ]).then(([allGroups, projectGroups]) =>
          {
            setGroups(allGroups || []);
            const selected = projectGroups || [];
            if (existingGroups.length === 0 && selected.length > 0)
            {
              setGroupsSelected(selected);
              setGroupIdsSelected(selected.map((g: any) => g.GroupId ?? g.Id));
            }
            setShowGroups(true);
          });
        }
        else
        {
          // For non-projects: fetch project groups as both options and selected
          duplicateCardService.GetGroups(conv.ProjectId).then(data =>
          {
            const fetchedGroups = data || [];
            setGroups(fetchedGroups);
            if (existingGroups.length === 0 && fetchedGroups.length > 0)
            {
              setGroupsSelected(fetchedGroups);
              setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
            }
            setShowGroups(true);
          });
        }

        if (conv.initialFiles && conv.initialFiles.length > 0)
        {
          setConversation((prev: any) => ({
            ...prev,
            Attachments: [...conv.initialFiles],
          }));
        }
      },
      [emptyConversation],
    );

    useImperativeHandle(ref, () => ({
      openModal: (conv?: any) =>
      {
        if (conv && Object.keys(conv).length > 0)
        {
          InitConfig(conv);
        }

        setShowTitleWarning(false);
        setSaveWarning(null);
        setIsSaving(false);
        setIsUploadingFile(false);
        setIsEnabledSaveAndNew(false);

        if (modalRef.current)
        {
          modalRef.current.openModal(() =>
          {
            setTimeout(() =>
            {
              inputRef.current?.focus();
            }, 0);
          });
        }
      },
      closeModal: () =>
      {
        setConfirmCloseAction(true);
        // if (modalRef.current) {
        //     modalRef.current.closeModal();
        // }
        // setConversation({ ...emptyConversation });
      },
    }));

    const ReloadConfig = useCallback(() =>
    {
      setConversation({ ...emptyConversation });
      setGroups([]);
      setLayersSelected([]);
      setShowCustomFields(false);
      setGroupIdsSelected([]);
      setGroupsSelected([]);
      setShowGroups(false);

      const isProject = (emptyConversation.ConfigValueId ?? emptyConversation.ConfigValueID) === 9;

      if (isProject)
      {
        Promise.all([
          duplicateCardService.GetGroups(),
          duplicateCardService.GetGroups(emptyConversation.ProjectId),
        ]).then(([allGroups, projectGroups]) =>
        {
          setGroups(allGroups || []);
          const selected = projectGroups || [];
          setGroupsSelected(selected);
          setGroupIdsSelected(selected.map((g: any) => g.GroupId ?? g.Id));
          setShowGroups(true);
          setShowCustomFields(true);
        });
      }
      else
      {
        duplicateCardService.GetGroups(emptyConversation.ProjectId).then(data =>
        {
          const fetchedGroups = data || [];
          setGroups(fetchedGroups);
          setGroupsSelected(fetchedGroups);
          setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
          setShowGroups(true);
          setShowCustomFields(true);
        });
      }
    }, [emptyConversation]);

    useEffect(() =>
    {
      if (config.attachReactFunctions)
      {
        config.attachReactFunctions({
          initConfig: InitConfig,
          reloadConfig: ReloadConfig,
        });
      }

      // Initial fetch if this is active
      if (conversation.ProjectId)
      {
        const isProject = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;

        if (isProject)
        {
          Promise.all([
            duplicateCardService.GetGroups(),
            duplicateCardService.GetGroups(conversation.ProjectId),
          ]).then(([allGroups, projectGroups]) =>
          {
            setGroups(allGroups || []);
            const selected = projectGroups || [];
            if (selected.length > 0)
            {
              setGroupsSelected(selected);
              setGroupIdsSelected(selected.map((g: any) => g.GroupId ?? g.Id));
            }
            setShowGroups(true);
          });
        }
        else
        {
          duplicateCardService.GetGroups(conversation.ProjectId).then(data =>
          {
            const fetchedGroups = data || [];
            setGroups(fetchedGroups);
            if (fetchedGroups.length > 0)
            {
              setGroupsSelected(fetchedGroups);
              setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
            }
            setShowGroups(true);
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const Reset = () =>
    {
      setConversation((prev: any) => ({
        ...prev,
        Title: '',
        Body: '',
        Attachments: [],
        IsRedFlag: false,
      }));
      summernoteRef.current?.clearContent();
      fileUploadRef.current?.reset();
    };

    const onLoadDataCallback = (
      _elementRef: any,
      reactParamsObjects: any,
      _reactParamsFunctions: any,
    ) =>
    {
      setLayersCustomFieldsOriginal(JSON.parse(JSON.stringify(reactParamsObjects.data)));
    };

    const UpdateDetailsConversationReact = (selectedProject: any) =>
    {
      setLayersCustomFieldsChanges(selectedProject);
    };

    const uptateTitle = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
      const val = e.target.value;
      setConversation((prev: any) => ({ ...prev, Title: val }));
      setShowTitleWarning(val.trim() === '');
    };

    const onMarkAsRedFlag = (e: React.MouseEvent) =>
    {
      e.preventDefault();
      setConversation((prev: any) => ({ ...prev, IsRedFlag: !prev.IsRedFlag }));
    };

    const onChangeSummernote = (content: string) =>
    {
      const safeContent = content.replace('&amp;', '&');
      setConversation((prev: any) => ({ ...prev, Body: safeContent }));
    };

    const onChangeFileUpload = (filesUploaded: boolean | any[]) =>
    {
      if (filesUploaded === true)
      {
        setIsUploadingFile(true);
      }
      else
      {
        setConversation((prev: any) => ({ ...prev, Attachments: filesUploaded as any[] }));
        setIsUploadingFile(false);
      }
    };

    const onRemoveAttachment = (index: number) =>
    {
      setConversation((prev: any) =>
      {
        const newAtt = [...(prev.Attachments || [])];
        newAtt.splice(index, 1);
        return { ...prev, Attachments: newAtt };
      });
    };

    const onUpdateTemporalImportant = (imp: boolean) =>
    {
      setIsImportant(imp);
    };

    const getTagsInBody = (bodyTags: string) =>
    {
      // simplified version. Original relied on jQuery parsing `$('<div>' + bodyTags + '</div>')`
      const listTags: any[] = [];
      try
      {
        const matchTags = bodyTags.match(/person-id="(\d+)"/g);
        if (matchTags)
        {
          matchTags.forEach(m =>
          {
            const id = m.replace(/[^0-9]/g, '');
            listTags.push({
              PersonId: parseInt(id, 10),
              ConversationId: 0,
              ReplyId: 0,
              ReplyFollowing: 0,
            });
          });
        }
      }
      catch (e)
      {
        console.warn(e);
      }
      return listTags;
    };

    const onSaveNewCard = async () =>
    {
      if (isUploadingFile || isSaving) return;
      setIsSaving(true);
      setSaveWarning(null);

      const newConv = { ...conversation };

      // The editor propagates its content debounced; read it directly on save.
      const liveBody = summernoteRef.current?.getContent?.();
      if (typeof liveBody === 'string') newConv.Body = liveBody;

      if (!newConv.Title || newConv.Title.trim() === '')
      {
        setShowTitleWarning(true);
        setIsSaving(false);
        return;
      }

      if (byteLength(newConv.Body || '') > MAX_BODY_BYTES)
      {
        setSaveWarning(BODY_TOO_LARGE_MESSAGE);
        setIsSaving(false);
        return;
      }

      if (showLayerWarning)
      {
        setSaveWarning('Select at least one layer before saving.');
        setIsSaving(false);
        return;
      }

      newConv.Tags = getTagsInBody(newConv.Body || '');
      newConv.CustomFields = [];

      // Custom Fields
      const customsfieldsOriginal: any[] = [];
      layersCustomFieldsOriginal.forEach(layer =>
      {
        if (layer)
        {
          layer.CustomFields?.forEach((cf: any) =>
          {
            customsfieldsOriginal.push({
              CustomFieldId: cf.CustomFieldId,
              Value: cf.Value,
              CoreTaskID: cf.CoreTaskId,
              FieldTypeId: cf.FieldTypeId,
              AssociatedImage: cf.AssociatedImage,
              CustomFieldValueId: cf.CustomFieldValueId,
            });
          });
        }
      });

      if (layersCustomFieldsChanges && layersCustomFieldsChanges.length > 0)
      {
        layersCustomFieldsChanges.forEach(layer =>
        {
          layer.CustomFields?.forEach((customField: any) =>
          {
            const cfb = customsfieldsOriginal.find(
              e => e.CustomFieldId === customField.CustomFieldId,
            );
            if (cfb)
            {
              if (cfb.Value !== customField.Value)
              {
                newConv.CustomFields.push({
                  CustomFieldId: customField.CustomFieldId,
                  Value: customField.Value,
                  CoreTaskID: customField.CoreTaskId || 0,
                  FieldTypeId: customField.FieldType
                    ? customField.FieldType.FieldTypeId
                    : customField.FieldTypeId,
                  AssociatedImage: customField.AssociatedImage,
                });
              }
            }
          });
        });
      }

      // Layers
      newConv.ConversationLayers = layersSelected.map(layer => ({
        LayerId: layer.LayerId,
        LayerColor: layer.Color,
        LayerName: layer.Name,
      }));

      // Groups
      newConv.ConversationGroups = [];
      if (newConv.ConfigValueId === 9)
      {
        groupsSelected.forEach((group: any) =>
        {
          newConv.ConversationGroups.push({
            GroupId: group.GroupId || group.Id,
            GroupName: group.Name,
          });
        });
      }
      else
      {
        groups.forEach((group: any) =>
        {
          newConv.ConversationGroups.push({
            GroupId: group.GroupId || group.Id,
            GroupName: group.Name,
          });
        });
      }

      if (config.functions.loadingBarStart)
      {
        config.functions.loadingBarStart();
      }

      try
      {
        // 1. Create Conversation
        const response = await duplicateCardService.SaveNewCard(newConv);
        if (response && response.Conversation)
        {
          response.Conversation.DashboardGroupId = newConv.DashboardGroupId;
          config.functions.addConversation &&
            config.functions.addConversation(response.Conversation, newConv.DashboardGroupId);

          // 2. Insert into Dashboard Group
          if (config.DashboardId)
          {
            await dashboardGroupsService.insertDashboardGroupConversation({
              DashboardId: Number(config.DashboardId),
              DashboardGroupId: newConv.DashboardGroupId,
              ConversationId: response.Conversation.ConversationId,
              ParentConversationId: 0,
              PreviousParentId: Number(newConv.ConfigValueId), // Using config ID as PreviousParentId based on example
              socketConnectionId: String(config.SocketConnectionId),
            });
            // 3. Update Percent (Stats)
            await dashboardGroupsService.updatePercentDashboardGroupConversations({
              dashboardGroupConvs: [{ DashboardGroupId: newConv.DashboardGroupId }],
              DashboardGroupIdDefault: 0,
            });
          }
        }
        else
        {
          // Keep the modal open instead of closing as if it had been saved
          setSaveWarning(DUPLICATE_FAILED_MESSAGE);
          setIsSaving(false);
          if (config.functions.loadingBarComplete) config.functions.loadingBarComplete();
          return;
        }

        if (modalRef.current && !isEnabledSaveAndNew) modalRef.current.closeModal();
        setIsSaving(false);
        if (isEnabledSaveAndNew)
        {
          // Keep project, configValueId, layers, groups and custom fields — only clear content fields
          Reset();
          setTimeout(() => inputRef.current?.focus(), 0);
        }
        else
        {
          setConversation({ ...emptyConversation });
          Reset();
        }
      }
      catch (err)
      {
        console.warn(err);
        setSaveWarning(DUPLICATE_FAILED_MESSAGE);
        setIsSaving(false);
        setIsUploadingFile(false);
        if (config.functions.loadingBarComplete) config.functions.loadingBarComplete();
      }
    };

    // Project selection
    const onChangeProject = (selected: any) =>
    {
      setSelectedProject(selected);
      const projectId = selected ? (selected.ProjectId ?? selected.Id) : null;
      setConversation((prev: any) => ({ ...prev, ProjectId: projectId }));
      if (projectId)
      {
        duplicateCardService.GetGroups(projectId).then(data =>
        {
          const fetchedGroups = data || [];
          setGroups(fetchedGroups);
          setGroupsSelected(fetchedGroups);
          setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
        });
      }
    };

    // Groups interactions
    const onChangeGroup = (newValue: MultiValue<any>) =>
    {
      const itemsSelected = [...newValue];
      setGroupIdsSelected(itemsSelected.map((i: any) => i.GroupId ?? i.Id));
      setGroupsSelected(itemsSelected);
    };

    // Layers interactions (react-select MultiValue)
    const onChangeLayerSelect = (newValue: MultiValue<any>) =>
    {
      const itemsSelected = [...newValue];
      setLayersSelected(itemsSelected);
      setShowLayerWarning(itemsSelected.length === 0);
      if (summernoteRef.current?.getPeopleTagList)
      {
        summernoteRef.current.getPeopleTagList();
      }
    };

    // Available layer options: for projects, derive from SELECTED groups' GroupLayers; otherwise from all groups
    const layerOptions = useMemo(() =>
    {
      const allLayers: any[] = [
        ...(conversation.ConversationLayers || []).filter((l: any) => l.LayerId !== 0),
      ];
      const isProject = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;

      // For projects: find the full group objects (with GroupLayers) from the catalog
      // because groupsSelected from GetGroupsByProject may not include GroupLayers
      let sourceGroups: any[];
      if (isProject)
      {
        const selectedGroupIds = new Set(groupsSelected.map((g: any) => g.GroupId ?? g.Id));
        sourceGroups = groups.filter((g: any) => selectedGroupIds.has(g.GroupId ?? g.Id));
      }
      else
      {
        sourceGroups = groups;
      }

      sourceGroups.forEach((group: any) =>
      {
        group.GroupLayers?.forEach((layer: any) =>
        {
          if (!allLayers.some((l: any) => l.LayerId === layer.LayerId))
          {
            allLayers.push(layer);
          }
        });
      });
      return allLayers;
    }, [
      conversation.ConversationLayers,
      conversation.ConfigValueId,
      conversation.ConfigValueID,
      groups,
      groupsSelected,
    ]);

    const isProjectCard = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;
    const modalTitle = isProjectCard
      ? 'Duplicate Project'
      : `Duplicate ${conversation.SideMenuText || 'Item'}`;

    const internalConfig: any = {
      showHeader: false,
      disableAutoSave: true,
      isNew: true,
      apiUrl: config.apiUrl,
      authToken: config.authToken,
      enableCmbProject: conversation.ProjectId == null,
      onLoadDataCallback,
      UpdateDetailsConversationReact,
      SocketConnectionId: config.SocketConnectionId,
    };

    const firstLayerColor =
      layersSelected && layersSelected.length > 0
        ? layersSelected[0].LayerColor || layersSelected[0].Color
        : '#ededed';
    const isDark = document.documentElement.classList.contains('dark');

    const inlineSelectStyles: StylesConfig<any, true> = {
      control: base => ({
        ...base,
        border: 'none',
        boxShadow: 'none',
        minHeight: '30px',
        backgroundColor: isDark ? '#2d2f2f' : 'transparent',
        cursor: 'text',
        '&:hover': { border: 'none' },
      }),
      valueContainer: base => ({
        ...base,
        padding: '3px 6px',
        flexWrap: 'wrap',
        gap: '3px',
      }),
      multiValue: base => ({
        ...base,
        backgroundColor: isDark ? '#3a3c3c' : '#fff',
        border: `1px solid ${isDark ? '#4a4c4c' : '#c4c4c4'}`,
        borderRadius: '3px',
        margin: '1px',
        fontSize: '11px',
        lineHeight: '1.4',
      }),
      multiValueLabel: base => ({
        ...base,
        color: isDark ? '#e5e0d8' : '#414042',
        padding: '2px 5px',
        fontSize: '11px',
      }),
      multiValueRemove: base => ({
        ...base,
        color: isDark ? '#6e6860' : '#999',
        padding: '0 4px',
        cursor: 'pointer',
        borderLeft: `1px solid ${isDark ? '#4a4c4c' : '#e0e0e0'}`,
        borderRadius: '0 3px 3px 0',
        ':hover': { backgroundColor: '#62accb', color: '#fff' },
      }),
      dropdownIndicator: () => ({ display: 'none' }),
      indicatorSeparator: () => ({ display: 'none' }),
      clearIndicator: () => ({ display: 'none' }),
      placeholder: base => ({ ...base, fontSize: '11px', color: isDark ? '#6e6860' : '#aaa' }),
      input: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
      menu: base => ({
        ...base,
        zIndex: 99999,
        fontSize: '11px',
        backgroundColor: isDark ? '#333535' : '#fff',
        border: isDark ? '1px solid #4a4c4c' : base.border,
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.2)',
        marginTop: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }),
      option: (base, state) => ({
        ...base,
        padding: '4px 8px',
        fontSize: '11px',
        backgroundColor: state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
        color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
        cursor: 'pointer',
      }),
      menuPortal: base => ({ ...base, zIndex: 999999 }),
    };

    const projectSelectStyles: StylesConfig<any, false> = {
      control: base => ({
        ...base,
        minHeight: '32px',
        fontSize: '12px',
        backgroundColor: isDark ? '#3a3c3c' : '#fff',
        borderColor: isDark ? '#4a4c4c' : '#ccc',
        boxShadow: 'none',
        '&:hover': { borderColor: isDark ? '#6e6860' : '#999' },
      }),
      singleValue: base => ({ ...base, fontSize: '12px', color: isDark ? '#a9a093' : base.color }),
      input: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
      option: (base, state) => ({
        ...base,
        fontSize: '12px',
        backgroundColor: state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
        color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
        cursor: 'pointer',
        padding: '5px 10px',
      }),
      menu: base => ({
        ...base,
        zIndex: 99999,
        fontSize: '12px',
        backgroundColor: isDark ? '#333535' : '#fff',
        border: isDark ? '1px solid #4a4c4c' : base.border,
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : base.boxShadow,
      }),
      menuPortal: base => ({ ...base, zIndex: 999999 }),
      placeholder: base => ({ ...base, fontSize: '12px', color: isDark ? '#6e6860' : '#aaa' }),
    };

    const projectSelectHeader =
      !isProjectCard && projects.length > 0 ? (
        <div style={{ width: '220px' }}>
          <Select
            value={selectedProject}
            options={projects}
            getOptionLabel={(o: any) => o.Name || o.ProjectName || ''}
            getOptionValue={(o: any) => String(o.ProjectId ?? o.Id)}
            onChange={onChangeProject}
            placeholder="Select project..."
            isDisabled={true}
            isClearable={false}
            styles={projectSelectStyles as StylesConfig<any, false>}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      ) : null;

    const validateClose = () =>
    {
      confirmCloseActionRef.current?.openModal();
      setShowLayerWarning(false);
      // setConfirmCloseAction(true);
      return false;
      //setConversation({ ...emptyConversation })
    };

    return (
      <div className="react-edit-project-window">
        <div id="editProjectInModal" className="tool-icon" onClick={() => {}}></div>
        <CorgeeModal
          className="react-new-ticket-modal"
          ref={modalRef}
          title={modalTitle}
          headerExtra={projectSelectHeader}
          onClose={() => validateClose()}
          onSave={onSaveNewCard}
          isDraggable={true}
          closeOnEscape={true}
          textButton="Create"
          height={680}
          width={620}
          saveButtonColor={firstLayerColor}
          allowSaveAndNewCheckBox={true}
          transparentBackdrop={true}
          callBackFunction={(checked: boolean) => setIsEnabledSaveAndNew(checked)}
          isSavingFile={isUploadingFile}
        >
          <div className="react-corgee-new_card-modal">
            <div className="react-corgee-new_ticket_title-line-newcard">
              <div className="react-corgee-item-icons-newcard">
                <span
                  className={
                    conversation.Icon
                      ? `identifier-icon-key ${conversation.Icon}`
                      : 'icons-menu-folder'
                  }
                ></span>
                {/* {!isProjectCard && (
                                <label className="custom-checkbox task-item DuplicateCard-finished-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={conversation.Finished || false}
                                        onChange={onChangeFinished}
                                        tabIndex={0}
                                    />
                                    <span></span>
                                </label>
                            )} */}
                <div className="title-card-action-icon red-flag-card-newcard">
                  <span
                    className={`corgee-icon-red-flag enable-help-tooltip ${conversation.IsRedFlag ? 'red' : ''}`}
                    title={conversation.IsRedFlag ? 'Unmark as Redflag' : 'Mark as Redflag'}
                    onClick={onMarkAsRedFlag}
                  ></span>
                </div>
              </div>
              <div className="react-corgee-item-title-input-newcard">
                <input
                  className="react-corgee-new_ticket_title"
                  onChange={uptateTitle}
                  type="text"
                  value={conversation.Title || ''}
                  placeholder="Type title here"
                  ref={inputRef}
                />
              </div>
            </div>
            <CorgeeWarning warningText="Enter a valid Title" show={showTitleWarning} />
            <CorgeeWarning warningText={saveWarning ?? ''} show={!!saveWarning} />

            {conversation?.HasPhases && (
              <div className="tabsH-wrapper" style={{ marginTop: '10px', marginBottom: '0px' }}>
                <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
                  <TabList>
                    <Tab className="react-tabs__tab">All Phases</Tab>
                    {(conversation.Phases || []).map((phase: any, index: number) => (
                      <Tab className="react-tabs__tab" key={phase.ConversationPhaseId || index}>
                        {editingPhaseId === phase.ConversationPhaseId ? (
                          <input
                            autoFocus
                            type="text"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              outline: 'none',
                              width: '100px',
                              fontSize: '13px',
                            }}
                            value={editingPhaseTitle}
                            onChange={e => setEditingPhaseTitle(e.target.value)}
                            onBlur={() => acceptPhaseRename(phase)}
                            onKeyDown={e =>
                            {
                              if (e.key === 'Enter') acceptPhaseRename(phase);
                              if (e.key === 'Escape') setEditingPhaseId(null);
                            }}
                          />
                        ) : (
                          <div
                            onDoubleClick={() =>
                            {
                              setEditingPhaseId(phase.ConversationPhaseId);
                              setEditingPhaseTitle(phase.Title || '');
                            }}
                            title="Double click to rename"
                          >
                            {phase.Title}
                          </div>
                        )}
                      </Tab>
                    ))}
                  </TabList>
                  <TabPanel />
                  {(conversation.Phases || []).map((_: any, idx: number) => (
                    <TabPanel key={`panel-${idx}`} />
                  ))}
                </Tabs>
              </div>
            )}

            {showCustomFields && (
              <div className="corgee-new-card-customfields">
                <CustomFields
                  section="section"
                  config={internalConfig as any}
                  item={conversation}
                  projectId={conversation.ProjectId}
                  configValueId={conversation.ConfigValueId}
                  groups={groups}
                  phaseSelected={activePhase}
                  isConversationDetailsView={false}
                  isProjectView={true}
                  UpdateCustomFields={UpdateCustomFields}
                />
              </div>
            )}

            {showSummernote && (
              <div className="react-summernote-new-ticket">
                <div className="card-content-container-new-card">
                  <SummernoteCorgee
                    // @ts-ignore
                    ref={summernoteRef}
                    conversationId={conversation.ConversationId}
                    onChangeBody={onChangeSummernote}
                    onUpdateTemporalImportant={onUpdateTemporalImportant}
                    body={conversation.Body}
                    layerColor={firstLayerColor}
                    textHeight={16}
                    layers={layersSelected}
                    api={api}
                    projectId={conversation.ProjectId}
                    configValueId={conversation.ConfigValueId}
                    selectorContainer=".card-content-container-new-card"
                    isAddReply={false}
                    fixedDropDownPosition={false}
                    isImportant={isImportant}
                    groupIdsSelected={groupIdsSelected.join(',')}
                  />
                  <div className="corgee-uploader" style={{ borderColor: firstLayerColor }}>
                    <FileUpload
                      ref={fileUploadRef}
                      initialFiles={conversation.Attachments || conversation.initialFiles || []}
                      onChangeFileUpload={onChangeFileUpload}
                      onRemoveAttachment={onRemoveAttachment}
                      config={api}
                    />
                  </div>
                </div>
              </div>
            )}

            {showGroups && isProjectCard && (
              <div className="publish-layers">
                <div className="publish-layers-widget">
                  <div className="dialog-item">
                    <div className="item-security-settings-container">
                      <div className="iss-label-groups">Security Groups</div>
                      <div className="iss-setting">
                        <div
                          className="corgee_com_card-layers-container-widget"
                          style={{ borderColor: firstLayerColor }}
                        >
                          <Select
                            isMulti
                            value={groupsSelected}
                            options={groups}
                            getOptionLabel={(o: any) => o.Name}
                            getOptionValue={(o: any) => String(o.GroupId ?? o.Id)}
                            onChange={onChangeGroup}
                            placeholder="Select a Group..."
                            isClearable={false}
                            closeMenuOnSelect={false}
                            styles={inlineSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showGroups && (
              <div className="publish-layers">
                <div className="publish-layers-widget">
                  <div className="dialog-item">
                    <div className="item-security-settings-container">
                      <div className="iss-label-layers">Layers</div>
                      <div className="iss-setting">
                        <div
                          className="corgee_com_card-layers-container-widget"
                          style={{ borderColor: firstLayerColor }}
                        >
                          {isProjectCard ? (
                            <Select
                              isMulti
                              value={layersSelected}
                              options={layerOptions}
                              getOptionLabel={(o: any) => o.Name || o.LayerName || ''}
                              getOptionValue={(o: any) => String(o.LayerId)}
                              onChange={onChangeLayerSelect}
                              placeholder="Select a Layer..."
                              isClearable={false}
                              closeMenuOnSelect={false}
                              styles={inlineSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          ) : (
                            <CardConversationLayers
                              apiConfig={{ url: config.apiUrl, token: config.authToken }}
                              conversation={conversation}
                              disableAutoSave={true}
                              isNew={true}
                              preserveInitialLayers={true}
                              isInlineWidget={true}
                              selectableConfigLayers={true}
                              customStyles={inlineSelectStyles}
                              onChangedLayers={(updatedConv: any) =>
                              {
                                const selected = updatedConv.ConversationLayers || [];
                                setLayersSelected(selected);
                                setShowLayerWarning(selected.length === 0);
                                if (summernoteRef.current?.getPeopleTagList)
                                {
                                  summernoteRef.current.getPeopleTagList();
                                }
                              }}
                              addLayerToCard={(
                                _e: any,
                                layer: any,
                                functions?: any,
                                _convId?: number,
                              ) =>
                              {
                                setPendingAddLayer(layer);
                                setPendingAddLayerCallback(() => functions);
                                addLayerModalRef.current?.openModal();
                              }}
                              addLayerToProject={handleAddLayerToProject}
                            />
                          )}
                        </div>
                        <CorgeeWarning
                          warningText="Select at least one layer."
                          show={showLayerWarning}
                        />
                        <CorgeeWarning
                          warningText="This project will be saved only in the selected security groups that have at least one of the selected layers."
                          show={showGroupWarning}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CorgeeModal>

        <CorgeeModal
          ref={addLayerModalRef}
          title="Add layer on card"
          textButton="Yes"
          cancelText="No"
          width={700}
          transparentBackdrop={true}
          onClose={() =>
          {
            setPendingAddLayer(null);
            setPendingAddLayerCallback(null);
          }}
          onSave={handleConfirmAddLayer}
          saveButtonColor="#62accb"
        >
          <div style={{ paddingTop: '10px' }}>
            <p style={{ marginBottom: '10px' }}>
              You must have {pendingAddLayer?.Name || pendingAddLayer?.LayerName} layer on both this
              card and it&apos;s project card to be able to post in that layer
            </p>
            <p>Do you want to add the layer to the project as well as this card?</p>
          </div>
        </CorgeeModal>
        <CorgeeModal
          ref={confirmCloseActionRef}
          title="Confirm Action"
          textButton="Yes"
          cancelText="No"
          onClose={() =>
          {
            return;
          }}
          onSave={() =>
          {
            modalRef.current && modalRef.current.closeModal();
            setConversation({ ...emptyConversation });
            confirmCloseActionRef.current?.closeModal();
          }}
          saveButtonColor="#62accb"
          width={330}
          transparentBackdrop={true}
          // open={confirmCloseAction}
        >
          <p className="pt-[10px] pb-2 text-sm">
            Are you sure you want to delete? Draft will be missed.
          </p>
        </CorgeeModal>
      </div>
    );
  },
);

export default DuplicateCard;
